import React, { useState } from 'react';
import { useLexicon } from '../../hooks/useLexicon';
import plusWhite from '../../static/img/plusWhite.svg';
import whiteAiIcon from '../../static/WhiteAI.svg';
import toggleDown from '../../static/img/toggledown.svg';
import UpdateIcon from '../../static/img/update15purple.svg';
import TabContext from '@mui/lab/TabContext';
import RoundedBorderSubmitBtn from '../../components2/Buttons/RoundedBorderSubmitBtn';
import {
  PageContainer,
  HeaderRow,
  ContentRow,
  LeftPane,
  HeaderContent,
  Title,
  Subtitle,
  HeaderButton,
  IconInButton,
  IconChevronDown,
  StepsList,
  StepItem,
  StepNumber,
  StepContent,
  StepHeading,
  StepDescription,
  InlineStepButton,
  RightPane,
  QuestionSection,
  SectionLabel,
  QuestionInput,
  GenerateRow,
  ResponseSection,
  ResponseHeader,
  ResponseLabel,
  SubmitButton,
  SubmitButtonIcon,
  SubjectTaskTypeContainer,
  STTitle,
  STDetails,
  StyledBox,
  StyledMainTabList,
  StyledTab,
  TabText,
  HandwrittenTabsContainer,
  HandwrittenStepsBox,
  HandwrittenStepTabList,
  HandwrittenStepTab,
  HandwrittenStepPanel,
  HandwrittenTabPart,
  HandwrittenTabNumber,
  HandwrittenTabNumberText,
  HandwrittenStepText,
  HandwrittenUploadFilesContainer,
  HandwrittenUploadFilesText,
  HandwrittenBody,
  UpdateButton,
  Toolbar,
  ToolbarButton,
  ToolbarSelect,
  ToolbarDivider,
  ResponseBody,
  ZoomRow,
  ZoomLabel,
  ZoomSlider,
  ZoomSliderThumb,
  ZoomValue,
  ToolbarUnderline,
  ToolbarStrikethrough,
  FooterBar,
  FooterText,
} from './EmptyDraftsPage/style';

function UndoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M4 6h6a3 3 0 010 6H7M4 6l2-2M4 6l2 2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RedoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M12 6H6a3 3 0 000 6h3M12 6l-2-2M12 6l-2 2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function HandwrittenStepLabel({ number, text, active }) {
  return (
    <HandwrittenTabPart>
      <HandwrittenTabNumber>
        <HandwrittenTabNumberText active={active}>
          {number}
        </HandwrittenTabNumberText>
      </HandwrittenTabNumber>
      <HandwrittenStepText active={active}>{text}</HandwrittenStepText>
    </HandwrittenTabPart>
  );
}

function TypedPreview() {
  return (
    <>
      <Toolbar>
        <ToolbarButton type="button">
          <UndoIcon />
        </ToolbarButton>
        <ToolbarButton type="button">
          <RedoIcon />
        </ToolbarButton>
        <ToolbarDivider />
        <ToolbarSelect type="button">
          Open Sans
          <IconChevronDown src={toggleDown} alt="" />
        </ToolbarSelect>
        <ToolbarSelect type="button">
          18px
          <IconChevronDown src={toggleDown} alt="" />
        </ToolbarSelect>
        <ToolbarDivider />
        <ToolbarButton type="button">
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton type="button">
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton type="button">
          <ToolbarUnderline>U</ToolbarUnderline>
        </ToolbarButton>
        <ToolbarButton type="button">T</ToolbarButton>
        <ToolbarButton type="button">
          <ToolbarStrikethrough>T</ToolbarStrikethrough>
        </ToolbarButton>
        <ToolbarButton type="button">List</ToolbarButton>
      </Toolbar>

      <ResponseBody>Add your response here...</ResponseBody>
    </>
  );
}

function HandwrittenPreview() {
  return (
    <HandwrittenBody>
      <HandwrittenTabsContainer>
        <TabContext value="1">
          <HandwrittenStepsBox sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <HandwrittenStepTabList aria-label="handwritten upload steps">
              <HandwrittenStepTab
                label={
                  <HandwrittenStepLabel
                    number="1"
                    text="Upload Files"
                    active
                  />
                }
                value="1"
              />
              <HandwrittenStepTab
                isDisabled
                label={
                  <HandwrittenStepLabel
                    number="2"
                    text="Order Pages"
                    active={false}
                  />
                }
                value="2"
              />
              <HandwrittenStepTab
                isDisabled
                label={
                  <HandwrittenStepLabel
                    number="3"
                    text="Preview"
                    active={false}
                  />
                }
                value="3"
              />
            </HandwrittenStepTabList>
          </HandwrittenStepsBox>
          <HandwrittenStepPanel value="1">
            <HandwrittenUploadFilesContainer>
              <RoundedBorderSubmitBtn text="Select Files" isDisabled />
              <HandwrittenUploadFilesText>
                Or drop files here
              </HandwrittenUploadFilesText>
              <HandwrittenUploadFilesText>
                (Files should be in .pdf, .jpeg, .png or .heic/.heif format.
                Maximum file upload size: 100mb)
              </HandwrittenUploadFilesText>
            </HandwrittenUploadFilesContainer>
          </HandwrittenStepPanel>
        </TabContext>
      </HandwrittenTabsContainer>
    </HandwrittenBody>
  );
}

export default function EmptyDraftsPage({ onCreate }) {
  const { t, lt, isNZ } = useLexicon();
  const [dummyMainTab, setDummyMainTab] = useState('1');

  return (
    <PageContainer>
      <HeaderRow>
        <HeaderContent>
          <Title>Welcome to My Drafts</Title>
          <Subtitle>
            {lt(
              'Get your work marked in seconds with personalised feedback and curriculum-aligned grading'
            )}
          </Subtitle>
        </HeaderContent>
        <HeaderButton type="button" onClick={onCreate}>
          <IconInButton src={plusWhite} alt="" />
          Create my first draft
        </HeaderButton>
      </HeaderRow>

      <ContentRow>
        <LeftPane>
          <StepsList>
            <StepItem>
              <StepNumber primary>1</StepNumber>
              <StepContent>
                <StepHeading>Start a 'New Draft'</StepHeading>
                <StepDescription>
                  Click the '+ Create my first draft' button to create a new draft document.
                </StepDescription>
              </StepContent>
            </StepItem>

            <StepItem>
              <StepNumber>2</StepNumber>
              <StepContent>
                <StepHeading>{t('Customise your marking preferences')}</StepHeading>
                <StepDescription>
                  Select your specific unit of study and task type for the most
                  accurate feedback.
                </StepDescription>
              </StepContent>
            </StepItem>

            <StepItem>
              <StepNumber>3</StepNumber>
              <StepContent>
                <StepHeading>Add your question</StepHeading>
                <StepDescription>
                  Add the question, topic or instruction to which you are
                  responding.
                </StepDescription>
              </StepContent>
            </StepItem>

            <StepItem>
              <StepNumber>4</StepNumber>
              <StepContent>
                <StepHeading>Upload your response</StepHeading>
                <StepDescription>
                  Add your response, which can be typed or handwritten.
                </StepDescription>
              </StepContent>
            </StepItem>

            <StepItem>
              <StepNumber>5</StepNumber>
              <StepContent>
                <StepHeading>Submit for feedback</StepHeading>
                <StepDescription>
                  Click 'Submit Now' to generate expert feedback and marking on your work in seconds.
                </StepDescription>
              </StepContent>
            </StepItem>
          </StepsList>
        </LeftPane>

        <RightPane>
          <GenerateRow>
            <SubjectTaskTypeContainer>
              {!isNZ && (
                <>
                  <div>
                    <STTitle>State:</STTitle>
                    <STDetails>NSW</STDetails>
                  </div>
                  <span>|</span>
                </>
              )}
              <div>
                <STTitle>Subject:</STTitle>
                <STDetails>{isNZ ? 'NCEA Level 3 English' : 'English Advanced'}</STDetails>
              </div>
              <span>|</span>
              <div>
                <STTitle>Year:</STTitle>
                <STDetails>{isNZ ? '13' : '12'}</STDetails>
              </div>
              <span>|</span>
              <div>
                <STTitle>Task Type:</STTitle>
                <STDetails>Analytical Essay</STDetails>
              </div>
              <span>|</span>
              <div>
                <STTitle>Response Type:</STTitle>
                <STDetails>Extended Response</STDetails>
              </div>
              <UpdateButton onClick={() => false}>
                <img src={UpdateIcon} />
                Update
              </UpdateButton>
            </SubjectTaskTypeContainer>
          </GenerateRow>
          <QuestionSection>
            <SectionLabel>Question</SectionLabel>
            <QuestionInput>Start typing your question here...</QuestionInput>
          </QuestionSection>

          <ResponseSection>
            <ResponseHeader>
              <ResponseLabel>Response</ResponseLabel>
              <SubmitButton type="button">
                <SubmitButtonIcon src={whiteAiIcon} alt="" />
                Submit Now
              </SubmitButton>
            </ResponseHeader>
            <TabContext value={dummyMainTab}>
              <StyledBox>
                <StyledMainTabList
                  onChange={(_, value) => setDummyMainTab(value)}
                  aria-label="empty drafts response tabs"
                >
                  <StyledTab
                    label={<TabText active={dummyMainTab === '1'}>Typed</TabText>}
                    value="1"
                  />
                  <StyledTab
                    label={<TabText active={dummyMainTab === '2'}>Handwritten</TabText>}
                    value="2"
                  />
                </StyledMainTabList>
              </StyledBox>
            </TabContext>

            {dummyMainTab === '1' ? <TypedPreview /> : <HandwrittenPreview />}
          </ResponseSection>
        </RightPane>
      </ContentRow>

      <FooterBar>
        <FooterText>120 words</FooterText>
        <ZoomRow>
          <ZoomLabel>Zoom</ZoomLabel>
          <ZoomSlider>
            <ZoomSliderThumb />
          </ZoomSlider>
          <ZoomValue>100%</ZoomValue>
        </ZoomRow>
      </FooterBar>
    </PageContainer>
  );
}


  // <InlineStepButton type="button" onClick={onCreate}>
                    // <IconInButton src={plusWhite} alt="" />
                    // New Draft
    //               </InlineStepButton>

    //  <InlineStepButton type="button">
    //                 <SubmitButtonIcon src={whiteAiIcon} alt="" />
    //                 Submit Now
    //               </InlineStepButton>